// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Avatar, Badge, Stack, Text, UnstyledButton } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { isPendingIntakePatient } from '@polarissleep/shared';
import cx from 'clsx';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { Link } from 'react-router';
import { formatPatientNameWithPreferredName, getPatientPreferredName } from '../../../../lib/patients';
import classes from './PatientListItem.module.css';

function getInitials(patient: Patient): string {
  const name = patient.name?.[0];
  const preferred = getPatientPreferredName(name);
  const given = preferred ?? name?.given?.[0] ?? '';
  const family = name?.family ?? '';
  const initials = `${given.charAt(0)}${family.charAt(0)}`.toUpperCase();
  return initials || '?';
}

interface PatientListItemProps {
  patient: Patient;
  lastContact?: string;
  selected: boolean;
  onClick?: (patient: Patient) => void;
}

export function PatientListItem({ patient, lastContact, selected, onClick }: PatientListItemProps): JSX.Element {
  const id = patient.id ?? '';
  const displayName = formatPatientNameWithPreferredName(patient.name?.[0]) || 'Unknown patient';
  const initials = getInitials(patient);

  const content = (
    <>
      <Avatar
        color={selected ? 'polaris' : 'teal'}
        variant={selected ? 'filled' : 'light'}
        radius="md"
        size={36}
        className={classes.avatar}
      >
        {initials}
      </Avatar>
      <Stack gap={2} className={classes.text}>
        <Text className={classes.name} fw={600} truncate="end">
          {displayName}
        </Text>
        {isPendingIntakePatient(patient) && (
          <Badge size="xs" variant="light" color="yellow">
            Pending intake
          </Badge>
        )}
        <Text className={classes.lastContact} size="xs" truncate="end">
          Last Contact: {lastContact ? dayjs(lastContact).format('MMM D, YYYY') : '-'}
        </Text>
      </Stack>
    </>
  );

  if (onClick) {
    return (
      <UnstyledButton
        type="button"
        onClick={() => onClick(patient)}
        aria-current={selected ? 'page' : undefined}
        className={cx(classes.row, { [classes.selected]: selected })}
      >
        {content}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton
      component={Link}
      to={`/Patient/${id}`}
      className={cx(classes.row, { [classes.selected]: selected })}
      aria-current={selected ? 'page' : undefined}
    >
      {content}
    </UnstyledButton>
  );
}
